/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit'
import { readThemeValue, themeMock, themeParser } from 'scenes/comparison/scenes/report/theme'
import { DEFAULT_REPORT_OPTIONS } from 'scenes/comparison/scenes/report/report-config'
import {
  ClientContext,
  ClientContextKey,
  IReportDetails,
  IReportOptions,
} from 'scenes/comparison/scenes/report/report.types'
import { IRootState } from 'store'

export interface IReportState {
  insuredName?: string
  filter: any
  theme: any
  themes: any
  /** The executive report's type, shared by its settings drawer, preview and PDF. */
  reportOptions: IReportOptions
  /**
   * Client information for a tailored report, held apart from the policy evidence and
   * stored against the comparison it was given for: `comparisonKey` names the selected
   * policies, and answers for a different selection are discarded.
   */
  clientContext: { comparisonKey: string; answers: ClientContext }
  /** Who issues the executive reports; shared by every report type. */
  reportDetails: IReportDetails
  /** The report's PDF is being made; shared so every Download PDF button shows it. */
  pdfDownloading: boolean
}

export const initialState: IReportState = {
  theme: themeMock[0],
  themes: themeMock,
  filter: null,
  insuredName: 'Insured Business Name',
  reportOptions: DEFAULT_REPORT_OPTIONS,
  clientContext: { comparisonKey: '', answers: {} },
  reportDetails: { preparedBy: '', role: '', organisation: '', contact: '', reference: '' },
  pdfDownloading: false,
}

/** Names a comparison by its selected policies, whatever order they were picked in. */
export const comparisonKeyFor = (policyIds: (string | number)[]) =>
  policyIds.map(String).sort().join(',')

const reportSlice = createSlice({
  name: 'report',
  initialState,
  reducers: {
    setTheme: (state: IReportState, { payload }: PayloadAction<string>) => {
      const found = state.themes.find((t) => t.id === payload)
      // An id not in the list keeps the current theme rather than clearing it.
      if (found) state.theme = found
    },
    setThemes: (state: IReportState, { payload }: PayloadAction<string>) => {
      state.theme = payload
    },
    setInsuredName: (state: IReportState, { payload }: PayloadAction<string>) => {
      state.insuredName = payload
    },
    updateReportOptions: (
      state: IReportState,
      { payload }: PayloadAction<Partial<IReportOptions>>,
    ) => {
      state.reportOptions = { ...state.reportOptions, ...payload }
    },
    setClientAnswer: (
      state: IReportState,
      {
        payload,
      }: PayloadAction<{
        comparisonKey: string
        key: ClientContextKey | 'specialConditions'
        value: number | string | null | undefined
      }>,
    ) => {
      const { comparisonKey, key, value } = payload
      const answers =
        state.clientContext.comparisonKey === comparisonKey
          ? { ...state.clientContext.answers }
          : {}
      if (value === null || value === undefined || value === '') delete answers[key]
      else (answers as Record<string, number | string>)[key] = value
      state.clientContext = { comparisonKey, answers }
    },
    clearClientContext: (state: IReportState) => {
      state.clientContext = { comparisonKey: '', answers: {} }
    },
    updateReportDetails: (
      state: IReportState,
      { payload }: PayloadAction<Partial<IReportDetails>>,
    ) => {
      state.reportDetails = { ...state.reportDetails, ...payload }
    },
    setPdfDownloading: (state: IReportState, { payload }: PayloadAction<boolean>) => {
      state.pdfDownloading = payload
    },
  },
})

/** The client answers for this comparison; none if they were given for another one. */
export const getClientAnswers = (state: IRootState, comparisonKey: string): ClientContext =>
  state.report.clientContext.comparisonKey === comparisonKey
    ? state.report.clientContext.answers
    : EMPTY_ANSWERS

const EMPTY_ANSWERS: ClientContext = {}

export const getTheme = createSelector(
  (state: IRootState) => state.report.theme,
  // A theme that is missing (e.g. an id no longer in the list) or unreadable falls back to
  // the default rather than breaking every screen that draws a report.
  (reportTheme) => themeParser(readThemeValue(reportTheme?.value)),
)
export const getThemes = (state) => state.report.themes.map((theme) => theme.value)

export const {
  setTheme,
  setThemes,
  setInsuredName,
  updateReportOptions,
  setClientAnswer,
  clearClientContext,
  updateReportDetails,
  setPdfDownloading,
} = reportSlice.actions
export default reportSlice.reducer
